<script setup>
import { ref, nextTick, watch, onUnmounted } from 'vue'
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  currentImage: {
    type: String,
    default: ''
  },
  uploading: {
    type: Boolean,
    default: false
  },
  slug: {
    type: String,
    default: 'image'
  },
  initialAspectRatio: {
    type: Number,
    default: 1
  }
})

const emit = defineEmits(['upload', 'error'])

const rawImage = ref(null)
const cropper = ref(null)
const cropAspectRatio = ref(props.initialAspectRatio)
const cropRotation = ref(0)
const fileInput = ref(null)

function onFileChange(e) {
  const file = e.target.files?.[0]
  if (!file) return

  const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
  if (!allowed.includes(file.type)) {
    emit('error', t('common.unsupportedFileType'))
    return
  }

  if (file.size > 10 * 1024 * 1024) {
    // 10MB limit
    emit('error', t('common.fileTooLarge', { max: '10MB' }))
    return
  }

  const url = URL.createObjectURL(file)
  rawImage.value = url
  cropAspectRatio.value = props.initialAspectRatio
  cropRotation.value = 0

  nextTick(() => {
    initCropper()
  })

  // clear input so same file can be selected again if cancelled
  e.target.value = ''
}

function initCropper() {
  if (cropper.value) {
    cropper.value.destroy()
    cropper.value = null
  }
  const img = document.getElementById('cropper-image-element')
  if (img) {
    cropper.value = new Cropper(img, {
      aspectRatio: cropAspectRatio.value,
      viewMode: 1, // Restrict crop box to not exceed the size of the canvas
      dragMode: 'move',
      background: true,
      autoCropArea: 0.8,
      responsive: true,
      restore: false,
      guides: true,
      center: true,
      highlight: false,
      cropBoxMovable: true,
      cropBoxResizable: true,
      toggleDragModeOnDblclick: false,
      minContainerWidth: 200,
      minContainerHeight: 200
    })
  }
}

function setAspectRatio(ratio) {
  cropAspectRatio.value = ratio
  if (cropper.value) {
    cropper.value.setAspectRatio(ratio)
  }
}

function zoomIn() {
  if (cropper.value) {
    cropper.value.zoom(0.1)
  }
}

function zoomOut() {
  if (cropper.value) {
    cropper.value.zoom(-0.1)
  }
}

function rotateLeft() {
  if (cropper.value) {
    cropRotation.value = (cropRotation.value - 90) % 360
    cropper.value.rotate(-90)
  }
}

function rotateRight() {
  if (cropper.value) {
    cropRotation.value = (cropRotation.value + 90) % 360
    cropper.value.rotate(90)
  }
}

function flipHorizontal() {
  if (cropper.value) {
    const imageData = cropper.value.getImageData()
    cropper.value.scaleX(-imageData.scaleX || -1)
  }
}

function flipVertical() {
  if (cropper.value) {
    const imageData = cropper.value.getImageData()
    cropper.value.scaleY(-imageData.scaleY || -1)
  }
}

function resetCropper() {
  if (cropper.value) {
    cropper.value.reset()
    cropRotation.value = 0
    cropAspectRatio.value = props.initialAspectRatio
  }
}

function cancelCrop() {
  if (cropper.value) {
    cropper.value.destroy()
    cropper.value = null
  }
  if (rawImage.value) {
    URL.revokeObjectURL(rawImage.value)
    rawImage.value = null
  }
  cropAspectRatio.value = props.initialAspectRatio
  cropRotation.value = 0
}

async function handleUpload() {
  if (!cropper.value) return

  try {
    const canvas = cropper.value.getCroppedCanvas({
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high'
    })

    if (!canvas) throw new Error('Canvas not created')

    const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.95))
    if (!blob) throw new Error('Blob creation failed')

    emit('upload', blob)
  } catch (e) {
    console.error(e)
    emit('error', 'Failed to prepare image for upload')
  }
}

// Cleanup object URL
onUnmounted(() => {
  if (rawImage.value) {
    URL.revokeObjectURL(rawImage.value)
  }
})

watch(
  () => props.currentImage,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      cancelCrop()
    }
  }
)

defineExpose({ reset: cancelCrop })
</script>

<template>
  <div class="image-uploader">
    <div v-if="!rawImage">
      <input
        type="file"
        accept="image/*"
        @change="onFileChange"
        class="file-input"
        id="image-input"
        ref="fileInput"
      />
      <label for="image-input" class="upload-label">
        📷 {{ t('common.chooseImage') || 'Choose Image' }}
      </label>

      <div v-if="currentImage" class="current-image-preview">
        <p class="hint">{{ t('admin.currentImage') || 'Current Image' }}</p>
        <img :src="currentImage" alt="Current" />
      </div>
    </div>

    <div v-else class="image-editor-container">
      <div class="aspect-ratio-controls">
        <span class="aspect-label">{{ t('admin.aspectRatio') || 'Aspect Ratio' }}:</span>
        <div class="aspect-buttons">
          <button
            type="button"
            @click="setAspectRatio(1)"
            :class="{ active: cropAspectRatio === 1 }"
            class="aspect-btn"
            title="Square 1:1"
          >
            ⬜ 1:1
          </button>
          <button
            type="button"
            @click="setAspectRatio(4 / 3)"
            :class="{ active: cropAspectRatio === 4 / 3 }"
            class="aspect-btn"
            title="Landscape 4:3"
          >
            ▭ 4:3
          </button>
          <button
            type="button"
            @click="setAspectRatio(16 / 9)"
            :class="{ active: cropAspectRatio === 16 / 9 }"
            class="aspect-btn"
            title="Wide 16:9"
          >
            ▬ 16:9
          </button>
          <button
            type="button"
            @click="setAspectRatio(3 / 4)"
            :class="{ active: cropAspectRatio === 3 / 4 }"
            class="aspect-btn"
            title="Portrait 3:4"
          >
            ▯ 3:4
          </button>
          <button
            type="button"
            @click="setAspectRatio(2 / 3)"
            :class="{ active: cropAspectRatio === 2 / 3 }"
            class="aspect-btn"
            title="Portrait 2:3"
          >
            ▯ 2:3
          </button>
          <button
            type="button"
            @click="setAspectRatio(NaN)"
            :class="{ active: isNaN(cropAspectRatio) }"
            class="aspect-btn"
            title="Free"
          >
            ⊡ {{ t('admin.free') || 'Free' }}
          </button>
        </div>
      </div>

      <div class="cropper-wrapper">
        <img :src="rawImage" id="cropper-image-element" alt="crop" />
      </div>

      <div class="cropper-controls">
        <div class="control-group">
          <label class="control-label">Zoom:</label>
          <button type="button" @click="zoomIn" class="control-btn" title="Zoom In">🔍+</button>
          <button type="button" @click="zoomOut" class="control-btn" title="Zoom Out">🔍-</button>
        </div>
        <div class="control-group">
          <label class="control-label">Rotate:</label>
          <button type="button" @click="rotateLeft" class="control-btn" title="Rotate Left">
            ↺
          </button>
          <button type="button" @click="rotateRight" class="control-btn" title="Rotate Right">
            ↻
          </button>
        </div>
        <div class="control-group">
          <label class="control-label">Flip:</label>
          <button type="button" @click="flipHorizontal" class="control-btn" title="Flip Horizontal">
            ⇄
          </button>
          <button type="button" @click="flipVertical" class="control-btn" title="Flip Vertical">
            ⇅
          </button>
        </div>
      </div>

      <div class="upload-actions">
        <button type="button" @click="resetCropper" class="btn-secondary" title="Reset">⟲</button>
        <button type="button" @click="cancelCrop" class="btn-secondary" title="Cancel">
          ✕ {{ t('common.cancel') || 'Cancel' }}
        </button>
        <button type="button" @click="handleUpload" :disabled="uploading" class="crop-btn">
          {{
            uploading
              ? (t('common.uploading') || 'Uploading') + '...'
              : '✂️ ' + (t('admin.cropAndUpload') || 'Crop & Upload')
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.file-input {
  display: none;
}

.upload-label {
  display: inline-block;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border, rgba(255, 255, 255, 0.1));
  color: inherit;
  padding: 0.6rem 1.2rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  margin-bottom: 1rem;
}

.upload-label:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.image-editor-container {
  margin-top: 1rem;
  width: 100%;
}

:deep(.cropper-container) {
  width: 100% !important;
}

.aspect-ratio-controls {
  margin-bottom: 1rem;
}

.aspect-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  opacity: 0.8;
}

.aspect-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.aspect-btn {
  padding: 0.3rem 0.6rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border, rgba(255, 255, 255, 0.1));
  border-radius: 6px;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s;
  color: inherit;
  font-weight: 500;
}

.aspect-btn:hover {
  background: rgba(224, 126, 39, 0.2);
  border-color: rgb(224, 126, 39);
}

.aspect-btn.active {
  background: rgb(224, 126, 39);
  color: #000;
  border-color: rgb(224, 126, 39);
  font-weight: 600;
}

.cropper-wrapper {
  max-width: 100%;
  max-height: 400px;
  border: 1px solid var(--border, rgba(255, 255, 255, 0.1));
  border-radius: 12px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
  margin-bottom: 1rem;
  position: relative;
}

.cropper-wrapper img {
  max-width: 100%;
  max-height: 400px;
  display: block;
  width: auto;
  height: auto;
}

.cropper-controls {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  margin-right: 0.5rem;
}

.control-label {
  font-size: 0.65rem;
  font-weight: 600;
  opacity: 0.7;
  display: none; /* simple icons only */
}

@media (min-width: 600px) {
  .control-label {
    display: block;
  }
}

.control-btn {
  padding: 0.3rem 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border, rgba(255, 255, 255, 0.1));
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  color: inherit;
  min-width: 28px;
}

.control-btn:hover {
  background: rgba(224, 126, 39, 0.2);
  border-color: rgb(224, 126, 39);
}

.upload-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.crop-btn {
  flex: 1;
  background: rgb(224, 126, 39);
  color: #000;
  border: none;
  padding: 0.7rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.75rem;
}

.crop-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(224, 126, 39, 0.3);
}

.crop-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 0.7rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border, rgba(255, 255, 255, 0.1));
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: inherit;
  font-size: 0.75rem;
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.current-image-preview {
  margin-top: 1rem;
}

.current-image-preview img {
  max-width: 100%;
  max-height: 300px;
  border: 1px solid var(--border, rgba(255, 255, 255, 0.1));
  border-radius: 12px;
  display: block;
  object-fit: contain;
}

.hint {
  font-size: 0.65rem;
  opacity: 0.6;
  margin: 0.5rem 0;
  line-height: 1.4;
}
</style>
